import type { Entry, RedirectMap } from "../../shared/snapshot-format";
import { loadCatalogue } from "./catalogue";
import { DATA_ROOT, fetchJson, loadManifest } from "./manifest";

const shardCache = new Map<string, Promise<Map<string, Entry>>>();
let redirects: Promise<RedirectMap> | undefined;

function loadShard(path: string): Promise<Map<string, Entry>> {
  let p = shardCache.get(path);
  if (!p) {
    p = fetchJson<Entry[]>(`${DATA_ROOT}/${path}`).then((list) => new Map(list.map((e) => [e.uuid, e])));
    shardCache.set(path, p);
  }
  return p;
}

/** The uuid-redirects map shipped with the Snapshot, fetched once. */
export function loadRedirects(): Promise<RedirectMap> {
  redirects ??= loadManifest().then((m) => fetchJson<RedirectMap>(`${DATA_ROOT}/${m.redirects.path}`));
  return redirects;
}

/** Follow uuid redirects from earlier Snapshots to the current UUID, if any. */
export async function resolveUuid(uuid: string): Promise<string> {
  const map = await loadRedirects();
  let cur = uuid;
  for (let i = 0; i < 8 && map[cur]; i++) cur = map[cur]!;
  return cur;
}

/** Load one Entry by UUID. Only the shard holding it is fetched, then kept in memory. */
export async function loadEntry(uuid: string): Promise<Entry | undefined> {
  const [manifest, catalogue] = await Promise.all([loadManifest(), loadCatalogue()]);
  const target = await resolveUuid(uuid);
  const rec = catalogue.find((r) => r.u === target);
  if (!rec) return undefined;
  const pack = manifest.packs.find((p) => p.id === rec.p);
  const shard = pack?.shards[rec.sh];
  if (!shard) throw new Error(`manifest has no shard ${rec.sh} for pack ${rec.p}`);
  return (await loadShard(shard.path)).get(target);
}
